/**
 * Health Conditions Constants
 *
 * Comprehensive list of health conditions for self-referral form selection.
 * These constants match the backend API expectations and provide consistent
 * string values throughout the application.
 */

import type { HealthConditionOption } from '@/components/self-referral/types';

/**
 * Health condition string constants
 * These exact strings are expected by the backend API
 */
export const HEALTH_CONDITIONS = {
  // Skin and Dermatological Conditions
  ACNE: 'acne',
  ECZEMA: 'eczema',
  PSORIASIS: 'psoriasis',
  ROSACEA: 'rosacea',
  CHRONIC_HIVES: 'chronic_hives',

  // Mental Health and Neurological Conditions
  ADHD: 'adhd',
  ANXIETY: 'anxiety',
  DEPRESSION_ANXIETY: 'depression_anxiety',
  ALZHEIMERS: 'alzheimers',
  DEMENTIA: 'dementia',
  AUTISM_SPECTRUM_DISORDER: 'autism_spectrum_disorder',
  EPILEPSY: 'epilepsy',
  MIGRAINE: 'migraine',
  MULTIPLE_SCLEROSIS: 'multiple_sclerosis',
  PARKINSONS_DISEASE: 'parkinsons_disease',

  // Metabolic and Endocrine Conditions
  TYPE_1_DIABETES: 'type_1_diabetes',
  TYPE_2_DIABETES: 'type_2_diabetes',
  GESTATIONAL_DIABETES: 'gestational_diabetes',
  PREDIABETES: 'prediabetes',
  METABOLIC_SYNDROME: 'metabolic_syndrome',
  HYPERTHYROIDISM: 'hyperthyroidism',
  HYPOTHYROIDISM: 'hypothyroidism',
  PCOS: 'pcos',
  CUSHINGS_SYNDROME: 'cushings_syndrome',
  ADRENAL_FATIGUE: 'adrenal_fatigue',

  // Cardiovascular Conditions
  HYPERTENSION: 'hypertension',
  HIGH_CHOLESTEROL: 'high_cholesterol',
  CARDIOVASCULAR_DISEASE: 'cardiovascular_disease',
  CONGESTIVE_HEART_FAILURE: 'congestive_heart_failure',
  STROKE_PREVENTION: 'stroke_prevention',

  // Gastrointestinal Conditions
  COELIAC_DISEASE: 'coeliac_disease',
  IBD: 'ibd',
  IBS: 'ibs',
  GERD: 'gerd',
  DIVERTICULOSIS: 'diverticulosis',
  GASTROPARESIS: 'gastroparesis',
  GALLBLADDER_DISEASE: 'gallbladder_disease',
  PANCREATITIS: 'pancreatitis',
  PEPTIC_ULCERS: 'peptic_ulcers',
  BILE_ACID_MALABSORPTION: 'bile_acid_malabsorption',
  SIBO: 'sibo',

  // Weight and Eating Disorders
  OVERWEIGHT_OBESITY: 'overweight_obesity',
  UNDERWEIGHT: 'underweight',
  DISORDERED_EATING: 'disordered_eating',
  ED_RECOVERY: 'ed_recovery',
  MUSCLE_GAIN: 'muscle_gain',
  MUSCLE_LOSS: 'muscle_loss',
  SARCOPENIA: 'sarcopenia',

  // Kidney and Liver Conditions
  CHRONIC_KIDNEY_DISEASE: 'chronic_kidney_disease',
  KIDNEY_STONES: 'kidney_stones',
  LIVER_DISEASE: 'liver_disease',
  LIVER_CIRRHOSIS: 'liver_cirrhosis',
  NEPHROTIC_SYNDROME: 'nephrotic_syndrome',

  // Cancer and Treatment-Related
  CANCER_RELATED_WEIGHT_LOSS: 'cancer_related_weight_loss',
  LOW_RESIDUE_CANCER_DIET: 'low_residue_cancer_diet',
  NEUTROPENIC_DIET: 'neutropenic_diet',
  MALNUTRITION: 'malnutrition',

  // Pregnancy and Women's Health
  PREGNANCY_TRIMESTER_ONE: 'pregnancy_trimester_one',
  PREGNANCY_TRIMESTER_TWO: 'pregnancy_trimester_two',
  PREGNANCY_TRIMESTER_THREE: 'pregnancy_trimester_three',
  BREASTFEEDING: 'breastfeeding',
  MENOPAUSE: 'menopause',
  FEMALE_FERTILITY: 'female_fertility',
  MALE_FERTILITY: 'male_fertility',
  ENDOMETRIOSIS: 'endometriosis',
  PMS: 'pms',
  PMDD: 'pmdd',

  // Bone and Joint Conditions
  OSTEOPOROSIS: 'osteoporosis',
  OSTEOPENIA: 'osteopenia',
  ARTHRITIS: 'arthritis',
  OSTEOARTHRITIS: 'osteoarthritis',
  RHEUMATOID_ARTHRITIS: 'rheumatoid_arthritis',
  GOUT: 'gout',

  // Autoimmune and Inflammatory Conditions
  LUPUS: 'lupus',
  MCAS: 'mcas',

  // Blood and Circulatory Conditions
  ANAEMIA: 'anaemia',
  HEMOCHROMATOSIS: 'hemochromatosis',
  ANTICOAGULANT_THERAPY: 'anticoagulant_therapy',

  // Surgical Conditions
  POST_BARIATRIC_SURGERY: 'post_bariatric_surgery',
  PRE_BARIATRIC_SURGERY: 'pre_bariatric_surgery',

  // Other Conditions
  ERECTILE_DYSFUNCTION: 'erectile_dysfunction'
} as const;

/**
 * Type for health condition values
 */
export type HealthCondition = typeof HEALTH_CONDITIONS[keyof typeof HEALTH_CONDITIONS];

/**
 * Health condition options with display labels and metadata
 * Organised by category for better user experience
 */
export const HEALTH_CONDITION_OPTIONS: HealthConditionOption[] = [
  // Common/Most Selected Conditions
  {
    id: HEALTH_CONDITIONS.TYPE_2_DIABETES,
    label: 'Type 2 Diabetes',
    description: 'Blood sugar management and dietary guidance',
    isCommon: true
  },
  {
    id: HEALTH_CONDITIONS.OVERWEIGHT_OBESITY,
    label: 'Overweight/Obesity',
    description: 'Healthy weight management strategies',
    isCommon: true
  },
  {
    id: HEALTH_CONDITIONS.HYPERTENSION,
    label: 'High Blood Pressure',
    description: 'Dietary approaches to blood pressure management',
    isCommon: true
  },
  {
    id: HEALTH_CONDITIONS.HIGH_CHOLESTEROL,
    label: 'High Cholesterol',
    description: 'Heart-healthy nutrition for cholesterol management',
    isCommon: true
  },
  {
    id: HEALTH_CONDITIONS.ANXIETY,
    label: 'Anxiety',
    description: 'Nutrition for mental wellness',
    isCommon: true
  },

  // Skin and Dermatological Conditions
  {
    id: HEALTH_CONDITIONS.ACNE,
    label: 'Acne',
    description: 'Anti-inflammatory nutrition for skin health'
  },
  {
    id: HEALTH_CONDITIONS.ECZEMA,
    label: 'Eczema',
    description: 'Skin-supportive nutrition and allergen management'
  },
  {
    id: HEALTH_CONDITIONS.PSORIASIS,
    label: 'Psoriasis',
    description: 'Anti-inflammatory diet for skin condition management'
  },
  {
    id: HEALTH_CONDITIONS.ROSACEA,
    label: 'Rosacea',
    description: 'Trigger avoidance and anti-inflammatory nutrition'
  },
  {
    id: HEALTH_CONDITIONS.CHRONIC_HIVES,
    label: 'Chronic Hives',
    description: 'Low-histamine diet and trigger identification'
  },

  // Mental Health and Neurological Conditions
  {
    id: HEALTH_CONDITIONS.ADHD,
    label: 'ADHD',
    description: 'Nutrition to support focus and cognitive function'
  },
  {
    id: HEALTH_CONDITIONS.DEPRESSION_ANXIETY,
    label: 'Depression & Anxiety',
    description: 'Mood-supporting nutrition strategies'
  },
  {
    id: HEALTH_CONDITIONS.ALZHEIMERS,
    label: 'Alzheimer\'s Disease',
    description: 'Brain-healthy nutrition and cognitive support'
  },
  {
    id: HEALTH_CONDITIONS.DEMENTIA,
    label: 'Dementia',
    description: 'Cognitive health and nutrition support'
  },
  {
    id: HEALTH_CONDITIONS.AUTISM_SPECTRUM_DISORDER,
    label: 'Autism Spectrum Disorder',
    description: 'Nutritional support for sensory and digestive issues'
  },
  {
    id: HEALTH_CONDITIONS.EPILEPSY,
    label: 'Epilepsy',
    description: 'Ketogenic and specialized dietary approaches'
  },
  {
    id: HEALTH_CONDITIONS.MIGRAINE,
    label: 'Migraine',
    description: 'Trigger avoidance and preventive nutrition'
  },
  {
    id: HEALTH_CONDITIONS.MULTIPLE_SCLEROSIS,
    label: 'Multiple Sclerosis',
    description: 'Anti-inflammatory nutrition for neurological health'
  },
  {
    id: HEALTH_CONDITIONS.PARKINSONS_DISEASE,
    label: 'Parkinson\'s Disease',
    description: 'Nutrition for movement and cognitive support'
  },

  // Metabolic and Endocrine Conditions
  {
    id: HEALTH_CONDITIONS.TYPE_1_DIABETES,
    label: 'Type 1 Diabetes',
    description: 'Insulin management and carbohydrate counting'
  },
  {
    id: HEALTH_CONDITIONS.GESTATIONAL_DIABETES,
    label: 'Gestational Diabetes',
    description: 'Blood sugar management during pregnancy'
  },
  {
    id: HEALTH_CONDITIONS.PREDIABETES,
    label: 'Prediabetes',
    description: 'Prevention strategies and lifestyle modifications'
  },
  {
    id: HEALTH_CONDITIONS.METABOLIC_SYNDROME,
    label: 'Metabolic Syndrome',
    description: 'Comprehensive metabolic health management'
  },
  {
    id: HEALTH_CONDITIONS.HYPERTHYROIDISM,
    label: 'Hyperthyroidism',
    description: 'Nutrition support for overactive thyroid'
  },
  {
    id: HEALTH_CONDITIONS.HYPOTHYROIDISM,
    label: 'Hypothyroidism',
    description: 'Nutrition support for underactive thyroid'
  },
  {
    id: HEALTH_CONDITIONS.PCOS,
    label: 'PCOS (Polycystic Ovary Syndrome)',
    description: 'Hormonal balance through nutrition'
  },
  {
    id: HEALTH_CONDITIONS.CUSHINGS_SYNDROME,
    label: 'Cushing\'s Syndrome',
    description: 'Managing cortisol-related nutritional needs'
  },
  {
    id: HEALTH_CONDITIONS.ADRENAL_FATIGUE,
    label: 'Adrenal Fatigue',
    description: 'Energy-supporting nutrition for adrenal health'
  },

  // Cardiovascular Conditions
  {
    id: HEALTH_CONDITIONS.CARDIOVASCULAR_DISEASE,
    label: 'Cardiovascular Disease',
    description: 'Heart-healthy nutrition and lifestyle'
  },
  {
    id: HEALTH_CONDITIONS.CONGESTIVE_HEART_FAILURE,
    label: 'Congestive Heart Failure',
    description: 'Sodium restriction and heart-supportive nutrition'
  },
  {
    id: HEALTH_CONDITIONS.STROKE_PREVENTION,
    label: 'Stroke Prevention',
    description: 'Vascular health and stroke prevention nutrition'
  },

  // Gastrointestinal Conditions
  {
    id: HEALTH_CONDITIONS.COELIAC_DISEASE,
    label: 'Coeliac Disease',
    description: 'Gluten-free diet management and nutrition'
  },
  {
    id: HEALTH_CONDITIONS.IBD,
    label: 'Inflammatory Bowel Disease (IBD)',
    description: 'Anti-inflammatory nutrition for Crohn\'s and UC'
  },
  {
    id: HEALTH_CONDITIONS.IBS,
    label: 'Irritable Bowel Syndrome (IBS)',
    description: 'FODMAP diet and digestive wellness'
  },
  {
    id: HEALTH_CONDITIONS.GERD,
    label: 'GERD (Acid Reflux)',
    description: 'Dietary modifications for reflux management'
  },
  {
    id: HEALTH_CONDITIONS.DIVERTICULOSIS,
    label: 'Diverticulosis',
    description: 'High-fibre diet and digestive health'
  },
  {
    id: HEALTH_CONDITIONS.GASTROPARESIS,
    label: 'Gastroparesis',
    description: 'Modified texture diet for delayed gastric emptying'
  },
  {
    id: HEALTH_CONDITIONS.GALLBLADDER_DISEASE,
    label: 'Gallbladder Disease',
    description: 'Low-fat diet and gallbladder health'
  },
  {
    id: HEALTH_CONDITIONS.PANCREATITIS,
    label: 'Pancreatitis',
    description: 'Low-fat, enzyme-supportive nutrition'
  },
  {
    id: HEALTH_CONDITIONS.PEPTIC_ULCERS,
    label: 'Peptic Ulcers',
    description: 'Stomach-soothing nutrition and healing support'
  },
  {
    id: HEALTH_CONDITIONS.BILE_ACID_MALABSORPTION,
    label: 'Bile Acid Malabsorption',
    description: 'Specialized diet for bile acid management'
  },
  {
    id: HEALTH_CONDITIONS.SIBO,
    label: 'SIBO (Small Intestinal Bacterial Overgrowth)',
    description: 'Low-FODMAP and specialized bacterial management'
  },

  // Weight and Eating Disorders
  {
    id: HEALTH_CONDITIONS.UNDERWEIGHT,
    label: 'Underweight',
    description: 'Healthy weight gain strategies'
  },
  {
    id: HEALTH_CONDITIONS.DISORDERED_EATING,
    label: 'Disordered Eating',
    description: 'Specialized nutrition therapy and support'
  },
  {
    id: HEALTH_CONDITIONS.ED_RECOVERY,
    label: 'Eating Disorder Recovery',
    description: 'Recovery-focused nutrition support'
  },
  {
    id: HEALTH_CONDITIONS.MUSCLE_GAIN,
    label: 'Muscle Gain',
    description: 'Protein-focused nutrition for muscle building'
  },
  {
    id: HEALTH_CONDITIONS.MUSCLE_LOSS,
    label: 'Muscle Loss',
    description: 'Nutrition to prevent muscle wasting'
  },
  {
    id: HEALTH_CONDITIONS.SARCOPENIA,
    label: 'Sarcopenia',
    description: 'Age-related muscle loss prevention'
  },

  // Kidney and Liver Conditions
  {
    id: HEALTH_CONDITIONS.CHRONIC_KIDNEY_DISEASE,
    label: 'Chronic Kidney Disease',
    description: 'Renal diet management and nutrition'
  },
  {
    id: HEALTH_CONDITIONS.KIDNEY_STONES,
    label: 'Kidney Stones',
    description: 'Prevention through dietary modifications'
  },
  {
    id: HEALTH_CONDITIONS.LIVER_DISEASE,
    label: 'Liver Disease',
    description: 'Hepatic nutrition management'
  },
  {
    id: HEALTH_CONDITIONS.LIVER_CIRRHOSIS,
    label: 'Liver Cirrhosis',
    description: 'Advanced liver disease nutrition support'
  },
  {
    id: HEALTH_CONDITIONS.NEPHROTIC_SYNDROME,
    label: 'Nephrotic Syndrome',
    description: 'Protein and fluid management for kidney health'
  },

  // Cancer and Treatment-Related
  {
    id: HEALTH_CONDITIONS.CANCER_RELATED_WEIGHT_LOSS,
    label: 'Cancer-Related Weight Loss',
    description: 'Nutrition support during cancer treatment'
  },
  {
    id: HEALTH_CONDITIONS.LOW_RESIDUE_CANCER_DIET,
    label: 'Low Residue Cancer Diet',
    description: 'Specialized diet for cancer treatment side effects'
  },
  {
    id: HEALTH_CONDITIONS.NEUTROPENIC_DIET,
    label: 'Neutropenic Diet',
    description: 'Food safety for immunocompromised patients'
  },
  {
    id: HEALTH_CONDITIONS.MALNUTRITION,
    label: 'Malnutrition',
    description: 'Nutritional rehabilitation and recovery'
  },

  // Pregnancy and Women's Health
  {
    id: HEALTH_CONDITIONS.PREGNANCY_TRIMESTER_ONE,
    label: 'Pregnancy - First Trimester',
    description: 'Early pregnancy nutrition and nausea management'
  },
  {
    id: HEALTH_CONDITIONS.PREGNANCY_TRIMESTER_TWO,
    label: 'Pregnancy - Second Trimester',
    description: 'Mid-pregnancy nutrition and growth support'
  },
  {
    id: HEALTH_CONDITIONS.PREGNANCY_TRIMESTER_THREE,
    label: 'Pregnancy - Third Trimester',
    description: 'Late pregnancy nutrition and preparation for birth'
  },
  {
    id: HEALTH_CONDITIONS.BREASTFEEDING,
    label: 'Breastfeeding',
    description: 'Lactation nutrition support'
  },
  {
    id: HEALTH_CONDITIONS.MENOPAUSE,
    label: 'Menopause',
    description: 'Nutrition for menopausal health'
  },
  {
    id: HEALTH_CONDITIONS.FEMALE_FERTILITY,
    label: 'Female Fertility',
    description: 'Fertility-supporting nutrition for women'
  },
  {
    id: HEALTH_CONDITIONS.MALE_FERTILITY,
    label: 'Male Fertility',
    description: 'Fertility-supporting nutrition for men'
  },
  {
    id: HEALTH_CONDITIONS.ENDOMETRIOSIS,
    label: 'Endometriosis',
    description: 'Anti-inflammatory nutrition for endometriosis'
  },
  {
    id: HEALTH_CONDITIONS.PMS,
    label: 'PMS (Premenstrual Syndrome)',
    description: 'Nutrition for menstrual cycle support'
  },
  {
    id: HEALTH_CONDITIONS.PMDD,
    label: 'PMDD (Premenstrual Dysphoric Disorder)',
    description: 'Specialized nutrition for severe PMS symptoms'
  },

  // Bone and Joint Conditions
  {
    id: HEALTH_CONDITIONS.OSTEOPOROSIS,
    label: 'Osteoporosis',
    description: 'Bone health nutrition strategies'
  },
  {
    id: HEALTH_CONDITIONS.OSTEOPENIA,
    label: 'Osteopenia',
    description: 'Bone density support and prevention'
  },
  {
    id: HEALTH_CONDITIONS.ARTHRITIS,
    label: 'Arthritis',
    description: 'Anti-inflammatory nutrition approaches'
  },
  {
    id: HEALTH_CONDITIONS.OSTEOARTHRITIS,
    label: 'Osteoarthritis',
    description: 'Joint health and inflammation management'
  },
  {
    id: HEALTH_CONDITIONS.RHEUMATOID_ARTHRITIS,
    label: 'Rheumatoid Arthritis',
    description: 'Autoimmune arthritis nutrition support'
  },
  {
    id: HEALTH_CONDITIONS.GOUT,
    label: 'Gout',
    description: 'Low-purine diet and uric acid management'
  },

  // Autoimmune and Inflammatory Conditions
  {
    id: HEALTH_CONDITIONS.LUPUS,
    label: 'Lupus',
    description: 'Anti-inflammatory nutrition for autoimmune health'
  },
  {
    id: HEALTH_CONDITIONS.MCAS,
    label: 'MCAS (Mast Cell Activation Syndrome)',
    description: 'Low-histamine diet and mast cell support'
  },

  // Blood and Circulatory Conditions
  {
    id: HEALTH_CONDITIONS.ANAEMIA,
    label: 'Anaemia',
    description: 'Iron-rich nutrition for blood health'
  },
  {
    id: HEALTH_CONDITIONS.HEMOCHROMATOSIS,
    label: 'Hemochromatosis',
    description: 'Iron management for iron overload'
  },
  {
    id: HEALTH_CONDITIONS.ANTICOAGULANT_THERAPY,
    label: 'Anticoagulant Therapy',
    description: 'Vitamin K management for blood thinners'
  },

  // Surgical Conditions
  {
    id: HEALTH_CONDITIONS.POST_BARIATRIC_SURGERY,
    label: 'Post-Bariatric Surgery',
    description: 'Post-surgical nutrition and supplement support'
  },
  {
    id: HEALTH_CONDITIONS.PRE_BARIATRIC_SURGERY,
    label: 'Pre-Bariatric Surgery',
    description: 'Pre-surgical nutrition preparation'
  },

  // Other Conditions
  {
    id: HEALTH_CONDITIONS.ERECTILE_DYSFUNCTION,
    label: 'Erectile Dysfunction',
    description: 'Vascular health nutrition for sexual wellness'
  }
];

/**
 * Get common health conditions (marked as isCommon: true)
 */
export const getCommonHealthConditions = (): HealthConditionOption[] => {
  return HEALTH_CONDITION_OPTIONS.filter(option => option.isCommon === true);
};

/**
 * Get all health conditions except common ones
 */
export const getOtherHealthConditions = (): HealthConditionOption[] => {
  return HEALTH_CONDITION_OPTIONS.filter(option => !option.isCommon);
};

/**
 * Find health condition option by ID
 */
export const findHealthConditionById = (id: string): HealthConditionOption | undefined => {
  return HEALTH_CONDITION_OPTIONS.find(option => option.id === id);
};

/**
 * Get health condition labels for selected IDs
 */
export const getHealthConditionLabels = (ids: string[]): string[] => {
  return ids
    .map(id => findHealthConditionById(id)?.label)
    .filter((label): label is string => label !== undefined);
};

/**
 * Validate health condition ID
 */
export const isValidHealthCondition = (id: string): id is HealthCondition => {
  return Object.values(HEALTH_CONDITIONS).includes(id as HealthCondition);
};

/**
 * Search health conditions by label or description
 */
export const searchHealthConditions = (query: string): HealthConditionOption[] => {
  const lowercaseQuery = query.toLowerCase();

  return HEALTH_CONDITION_OPTIONS.filter(option =>
    option.label.toLowerCase().includes(lowercaseQuery) ||
    option.description?.toLowerCase().includes(lowercaseQuery)
  );
};